import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronsDownUp, File, Folder, FolderOpen, Lock } from "lucide-react";

const CHILDREN_PAGE_SIZE = 100;
const EXPAND_ALL_FILE_LIMIT = 20;
const ROOT_KEY = "root";
// Must match .torrent-tree-children-wrapper transition in index.css
const COLLAPSE_ANIMATION_MS = 300;

const LOCKED_BRANCH_ROWS = [
  { folder: true, width: "46%" },
  { folder: false, width: "64%" },
  { folder: false, width: "34%" },
  { folder: false, width: "52%" },
];

const TreeStateContext = createContext({
  openKeys: new Set(),
  toggleFolder: () => {},
});

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exponent;
  return `${value >= 100 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

function createFolder(name, key) {
  return { type: "folder", name, key, size: 0, children: new Map() };
}

function finalizeFolder(folder) {
  const children = [...folder.children.values()].map((child) =>
    child.type === "folder" ? finalizeFolder(child) : child,
  );
  children.sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return collator.compare(a.name, b.name);
  });
  return { ...folder, children };
}

function buildTree({ name, isMultiFile, files }) {
  if (!isMultiFile) {
    return files.map((file, index) => ({
      type: "file",
      name: file.path[file.path.length - 1],
      key: `file:${index}`,
      size: file.length,
    }));
  }

  const root = createFolder(name, ROOT_KEY);
  files.forEach((file, index) => {
    let folder = root;
    folder.size += file.length;
    file.path.slice(0, -1).forEach((segment) => {
      const mapKey = `d:${segment}`;
      if (!folder.children.has(mapKey)) {
        folder.children.set(
          mapKey,
          createFolder(segment, `${folder.key}/${segment}`),
        );
      }
      folder = folder.children.get(mapKey);
      folder.size += file.length;
    });
    const fileName = file.path[file.path.length - 1];
    folder.children.set(`f:${index}`, {
      type: "file",
      name: fileName,
      key: `${folder.key}/${fileName}:${index}`,
      size: file.length,
    });
  });
  return [finalizeFolder(root)];
}

function collectFolderKeys(nodes, keys = []) {
  nodes.forEach((node) => {
    if (node.type !== "folder") return;
    keys.push(node.key);
    collectFolderKeys(node.children, keys);
  });
  return keys;
}

function getInitialOpenKeys(tree, fileCount) {
  if (fileCount <= EXPAND_ALL_FILE_LIMIT) {
    return new Set(collectFolderKeys(tree));
  }
  return new Set(
    tree.filter((node) => node.type === "folder").map((node) => node.key),
  );
}

function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

function pointerCanHover() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches
  );
}

function FileNode({ node, nested, current, onMouseEnter }) {
  return (
    <li className="torrent-tree-item">
      <div
        className={classNames(
          "torrent-tree-row",
          nested && "is-nested",
          current && "is-current",
        )}
        onMouseEnter={onMouseEnter}
      >
        <File className="torrent-tree-icon" aria-hidden="true" />
        <span className="torrent-tree-name">{node.name}</span>
        <span className="torrent-tree-meta">{formatBytes(node.size)}</span>
      </div>
    </li>
  );
}

// Children of a folder are mounted one level ahead of what is open,
// so the expand animation has content to reveal without mounting the whole tree.
function FolderNode({
  node,
  isVisible,
  depth,
  nested,
  current,
  onMouseEnter,
  onNestedEnter,
}) {
  const { openKeys, toggleFolder } = useContext(TreeStateContext);
  const open = openKeys.has(node.key);
  const shouldRenderChildren = open || isVisible;

  return (
    <li className={classNames("torrent-tree-item", open && "is-open")}>
      {depth === 0 && (
        <span className="torrent-tree-marker" aria-hidden="true" />
      )}
      <button
        type="button"
        className={classNames(
          "torrent-tree-row",
          nested ? "is-nested" : "is-head",
          open && "is-open",
          current && "is-current",
        )}
        aria-expanded={open}
        onClick={() => toggleFolder(node.key)}
        onMouseEnter={onMouseEnter}
      >
        {open ? (
          <FolderOpen
            className="torrent-tree-icon is-open"
            aria-hidden="true"
          />
        ) : (
          <Folder className="torrent-tree-icon" aria-hidden="true" />
        )}
        <span className="torrent-tree-name">{node.name}</span>
        <span className="torrent-tree-meta">{formatBytes(node.size)}</span>
      </button>
      <div
        className={classNames(
          "torrent-tree-children-wrapper",
          open && "is-open",
          depth === 0 && "is-root-level",
        )}
      >
        {shouldRenderChildren && (
          <TreeBranch
            nodes={node.children}
            isVisible={open}
            depth={depth + 1}
            branched
            onEnter={onNestedEnter}
          />
        )}
      </div>
    </li>
  );
}

function TreeBranch({ nodes, isVisible, depth, branched, onEnter }) {
  const [limit, setLimit] = useState(CHILDREN_PAGE_SIZE);
  const [hovered, setHovered] = useState(-1);
  const shownCount = Math.min(nodes.length, limit);
  const hiddenCount = nodes.length - shownCount;
  const hoverable = branched && pointerCanHover();
  const hoverAt = hoverable ? (index) => () => setHovered(index) : undefined;

  return (
    <ul
      className={branched ? "torrent-tree-children" : "torrent-tree-root"}
      onMouseEnter={hoverable ? onEnter : undefined}
      onMouseLeave={hoverable ? () => setHovered(-1) : undefined}
    >
      {nodes.slice(0, shownCount).map((node, index) =>
        node.type === "folder" ? (
          <FolderNode
            key={node.key}
            node={node}
            isVisible={isVisible}
            depth={depth}
            nested={branched}
            current={hovered === index}
            onMouseEnter={hoverAt?.(index)}
            onNestedEnter={hoverable ? () => setHovered(-1) : undefined}
          />
        ) : (
          <FileNode
            key={node.key}
            node={node}
            nested={branched}
            current={hovered === index}
            onMouseEnter={hoverAt?.(index)}
          />
        ),
      )}
      {hiddenCount > 0 && (
        <li className="torrent-tree-item">
          <button
            type="button"
            className={classNames(
              "torrent-tree-row",
              "torrent-tree-more",
              branched && "is-nested",
              hovered === shownCount && "is-current",
            )}
            onClick={() => setLimit((value) => value + CHILDREN_PAGE_SIZE)}
            onMouseEnter={hoverAt?.(shownCount)}
          >
            Show {Math.min(hiddenCount, CHILDREN_PAGE_SIZE)} more of{" "}
            {hiddenCount}
          </button>
        </li>
      )}
    </ul>
  );
}

function LockedPreview() {
  return (
    <div className="torrent-tree torrent-tree-locked-preview" aria-hidden="true">
      <div className="torrent-tree-row is-head">
        <Folder className="torrent-tree-icon" />
        <span className="torrent-tree-locked-bar" style={{ width: "40%" }} />
      </div>
      <div className="torrent-tree-children-wrapper is-open is-root-level">
        <ul className="torrent-tree-children">
          {LOCKED_BRANCH_ROWS.map((row, index) => (
            <li key={index} className="torrent-tree-item">
              <div className="torrent-tree-row is-nested">
                {row.folder ? (
                  <Folder className="torrent-tree-icon" />
                ) : (
                  <File className="torrent-tree-icon" />
                )}
                <span
                  className="torrent-tree-locked-bar"
                  style={{ width: row.width }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const TorrentFileTree = ({ torrentId, refreshKey = "", onLockedClick }) => {
  const [state, setState] = useState({ status: "loading", data: null });
  const [tree, setTree] = useState([]);
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [resetVersion, setResetVersion] = useState(0);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading", data: null });

    fetch(`/api/content/${torrentId}/files`, {
      headers: {
        Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
      },
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.available && data.locked) {
          setTree([]);
          setOpenKeys(new Set());
          setState({ status: "locked", data });
          return;
        }
        if (!data?.available || !data.files?.length) {
          setState({ status: "empty", data: null });
          return;
        }
        const nextTree = buildTree(data);
        setTree(nextTree);
        setOpenKeys(getInitialOpenKeys(nextTree, data.fileCount));
        setState({ status: "ready", data });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setState({ status: "empty", data: null });
        }
      });

    return () => controller.abort();
  }, [torrentId, refreshKey]);

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  const toggleFolder = useCallback((key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const hasFolders = tree.some((node) => node.type === "folder");
  const canCollapse = [...openKeys].some((key) => key !== ROOT_KEY);

  const collapseAll = () => {
    if (!canCollapse) return;
    setOpenKeys(new Set([ROOT_KEY]));
    // Remount after the collapse animation to also reset "Show more" paging
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(
      () => setResetVersion((version) => version + 1),
      COLLAPSE_ANIMATION_MS,
    );
  };

  const treeState = useMemo(
    () => ({ openKeys, toggleFolder }),
    [openKeys, toggleFolder],
  );

  if (state.status === "empty") return null;

  const data = state.data;
  const headerInteractive = Boolean(data) && hasFolders;

  return (
    <div
      className="p-6 m-4 rounded-xl"
      style={{
        backgroundColor: "#1a1a20",
        border: "1px solid rgba(255, 255, 255, 0.05)",
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-4">
        {headerInteractive ? (
          <h3 className="text-xl font-semibold">
            <button
              type="button"
              className={`torrent-tree-header${canCollapse ? " can-collapse" : ""}`}
              onClick={collapseAll}
              disabled={!canCollapse}
              title={canCollapse ? "Collapse all folders" : undefined}
            >
              Files
              <span className="torrent-tree-header-action">
                <ChevronsDownUp size={14} aria-hidden="true" />
                Collapse all
              </span>
            </button>
          </h3>
        ) : (
          <h3
            className="text-xl font-semibold flex items-center gap-2"
            style={{ color: "#999" }}
          >
            Files
            {state.status === "locked" && (
              <Lock size={16} className="torrent-tree-lock-icon" aria-hidden="true" />
            )}
          </h3>
        )}
        {data && (
          <span className="text-sm" style={{ color: "#666" }}>
            {data.fileCount} {data.fileCount === 1 ? "file" : "files"} •{" "}
            {formatBytes(data.totalSize)}
          </span>
        )}
      </div>

      {state.status === "loading" ? (
        <div style={{ color: "#999" }}>Loading files...</div>
      ) : state.status === "locked" ? (
        <button
          type="button"
          className="torrent-tree-locked"
          onClick={onLockedClick}
        >
          <LockedPreview />
          <span className="torrent-tree-locked-cta">
            <Lock size={14} aria-hidden="true" />
            Get access to view files
          </span>
        </button>
      ) : (
        <div className="torrent-tree">
          <TreeStateContext.Provider value={treeState}>
            <TreeBranch
              key={resetVersion}
              nodes={tree}
              isVisible
              depth={0}
              branched={
                tree.length > 1 && tree.every((node) => node.type === "file")
              }
            />
          </TreeStateContext.Provider>
          {data.isTruncated && (
            <div className="mt-3 text-sm" style={{ color: "#666" }}>
              Showing first {data.files.length} of {data.fileCount} files
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TorrentFileTree;
